export type MetricsRange = "1y" | "3y" | "5y" | "all";

const DAY_MS = 86400000;

export const RANGE_TO_DAYS: Record<Exclude<MetricsRange, "all">, number> = {
  "1y": 365,
  "3y": 3 * 365,
  "5y": 5 * 365,
};

/** Libellé d'axe X pour un point du graphique : « JJ/MM/AAAA » avec padding. */
export const formatChartDateLabel = (date: Date): string => {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/**
 * Construit la liste des plages disponibles.
 * Toutes les plages sont toujours proposées dès qu'il y a au moins une donnée ;
 * sans donnée on ne montre que « Tout ».
 */
export const computeAvailableRanges = (
  oldestEntryAt: number | null,
  _now?: number,
): MetricsRange[] => {
  if (oldestEntryAt === null || !Number.isFinite(oldestEntryAt)) {
    return ["all"];
  }
  return ["1y", "3y", "5y", "all"];
};

/** Borne basse (cutoff ms) pour filtrer les entrées d'une plage donnée. */
export const cutoffForRange = (
  range: MetricsRange,
  now: number = Date.now(),
): number => {
  if (range === "all") return Number.NEGATIVE_INFINITY;
  return now - RANGE_TO_DAYS[range] * DAY_MS;
};

export interface ResolveDefaultRangeInput {
  /** Plages disponibles d'après les données. */
  availableRanges: MetricsRange[];
  /** Plage actuellement sélectionnée. */
  currentRange: MetricsRange;
  /** `true` si le défaut « 1 an » a déjà été appliqué (ne plus écraser). */
  defaultApplied: boolean;
}

export interface ResolveDefaultRangeResult {
  /** Plage à utiliser ; identique à `currentRange` si aucun changement. */
  nextRange: MetricsRange;
  /** Nouvelle valeur du flag `defaultApplied`. */
  defaultApplied: boolean;
}

/**
 * Logique pure du useEffect d'auto-sélection :
 *  - si la sélection devient invalide (plage retirée), on retombe sur « all » ;
 *  - sinon on respecte le choix utilisateur (défaut = « Tout »).
 */
export const resolveDefaultRange = ({
  availableRanges,
  currentRange,
  defaultApplied,
}: ResolveDefaultRangeInput): ResolveDefaultRangeResult => {
  if (availableRanges.length === 0) {
    return { nextRange: "all", defaultApplied };
  }
  if (!availableRanges.includes(currentRange)) {
    return { nextRange: "all", defaultApplied };
  }
  return { nextRange: currentRange, defaultApplied };
};
